import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Logger } from "../../infra/logger/logger";
import equipmentTypeRepository from "../../infra/repositories/equipment-type-repository";
import { EquipmentType } from "../../domain/entities/equipment-type";

export const useEquipmentTypeByFilterAll = (filter: any) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["equipmentTypes", filter],
    queryFn: async () => {
      try {
        const result = await equipmentTypeRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] || "Error fetching equipment types",
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
  });
};

export const useCreateEquipmentTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EquipmentType) => {
      try {
        return await equipmentTypeRepository.create(data);
      } catch (error: any) {
        if (error instanceof AxiosError) {
          const message =
            error.response?.data?.metadata?.errors?.[0] ||
            "Não foi possível criar o tipo de equipamento.";
          logger.error(message, error.response?.data?.metadata);
          throw new Error(message);
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipmentTypes"] });
    },
  });
};

export const useUpdateEquipmentTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EquipmentType) => {
      const result = await equipmentTypeRepository.update(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipmentTypes"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || ["Error updating equipment type"];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useDeleteEquipmentTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await equipmentTypeRepository.delete(id);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipmentTypes"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || ["Error deleting equipment type"];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};
